// Single-player KPI page: Player Page.dc.html from the design project, which also covers the two-way page
// (Player Page - Jayson Kent.dc.html): with no NBA games the NBA block collapses to three goal cards, and his latest
// G League game sits below in the full layout, against G League goals and percentiles. One letter page.
// In a dashboard window the Tonight cell becomes the window (the design's dashboard note); the chart keeps 20 games.
import { CSSProperties } from "react";
import {
  blockStyle,
  buildPlayers,
  chart,
  fmt,
  gameMeta,
  KpiModel,
  MET,
  PlayerModel,
} from "./kpiLib";
import { Footer, Glyph } from "./common";
import { Headshot } from "./PlayerKpiPage";
import GameStrip from "./GameStrip";
import type { WindowHead } from "./common";
import type { KpiResponse } from "./types";

const DISP = "var(--pr-disp)";
const KICK: CSSProperties = {
  fontSize: 10,
  lineHeight: 1,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#6b6864",
  fontWeight: 700,
};
const BIG: CSSProperties = {
  fontFamily: DISP,
  fontWeight: 800,
  fontSize: 20,
  lineHeight: 1,
  whiteSpace: "nowrap",
  marginTop: 9,
};
const SUB: CSSProperties = {
  fontSize: 10,
  lineHeight: 1,
  color: "#6b6864",
  whiteSpace: "nowrap",
  letterSpacing: "-0.01em",
  marginTop: 8,
};
const LBL: CSSProperties = {
  position: "absolute",
  fontSize: 10,
  lineHeight: 1,
  color: "#6b6864",
  whiteSpace: "nowrap",
};
const pct = (v: number, d: number) => ((v / d) * 100).toFixed(2) + "%";

interface Block {
  title: string;
  sub: string;
  note: string;
  kpis: KpiModel[];
  full: boolean;
  tonightKicker: string;
  league: "NBA" | "G League";
  mt: string;
}

export default function PlayerPage({
  data,
  playerId,
  only,
  winHead,
}: {
  data: KpiResponse;
  playerId: string;
  /** A two-way's single game on the dashboard: that game's NBA block only, or his G League game's block only. */
  only?: "nba" | "gleague";
  /** A multi-game window's name, dates and record (dashboard). */
  winHead?: WindowHead | null;
}) {
  const game = data.meta.game!;
  const g = gameMeta(game, data.meta.porGamesToDate);
  const preseason = data.meta.seasonType === "preseason";
  const { players } = buildPlayers(data.players || []);
  const p = players.find((x) => x.playerId === playerId || x.id === playerId);
  if (!p) return null;
  const nbaHasData = p.kpis.some((k) => k.std.has || k.hasGame);
  // A dashboard window replaces "tonight" (the design's dashboard note).
  const win = p.kpis.find((k) => k.raw.window)?.raw.window || null;
  const blocks: Block[] = [];
  const base = { league: "NBA" as const, mt: "0" };
  if (win)
    blocks.push({
      ...base,
      title: "2026-27 KPIs",
      sub: `${win.label} · through ${g.dateLong}`,
      note: "NBA",
      kpis: p.kpis,
      full: true,
      tonightKicker: win.label,
    });
  else if (p.played)
    blocks.push({
      ...base,
      title: "2026-27 KPIs",
      sub: `Tonight · ${p.poss ?? "—"} poss`,
      note:
        p.status === "sample_other_team" ? `Sample · ${p.sampleGame}` : "NBA",
      kpis: p.kpis,
      full: true,
      tonightKicker: "Tonight",
    });
  else if (p.garbageOnly)
    blocks.push({
      ...base,
      title: "2026-27 KPIs",
      sub: `Garbage time only · ${p.garbagePoss ?? 0} poss`,
      note: "NBA",
      kpis: p.kpis,
      full: true,
      tonightKicker: "Tonight",
    });
  else if (nbaHasData)
    blocks.push({
      ...base,
      title: "2026-27 KPIs",
      sub: "Did not play tonight",
      note: "NBA",
      kpis: p.kpis,
      full: true,
      tonightKicker: "Tonight",
    });
  else
    blocks.push({
      ...base,
      title: "2026-27 KPIs · NBA",
      sub: "No NBA games this season",
      note: "",
      kpis: p.kpis,
      full: false,
      tonightKicker: "Tonight",
    });
  if (only === "gleague") blocks.length = 0;
  if (p.gleague && only !== "nba")
    blocks.push({
      title: "G League",
      sub: p.gleague.game
        ? `${only === "gleague" ? "Game" : "Latest game"} · ${p.gleague.game} · ${p.gleague.poss ?? "—"} poss`
        : "No G League games yet",
      note: `${p.twoWay ? "Two-way · " : ""}G League goals and percentiles`,
      kpis: p.gleague.kpis,
      full: true,
      tonightKicker: "G League game",
      league: "G League",
      mt: only === "gleague" ? "0" : "10px",
    });
  const two = blocks.length > 1;
  // The design fits a G League block under the NBA one only when the NBA block is the compact one (Kent); a player
  // with a full NBA block and a G League line (Hansen) gets his G League block on a second sheet.
  const split = two && blocks[0].full;
  const CW = 340;
  const CH = two && !split ? 156 : 212;
  const kpad = two && !split ? "8px 0 9px" : "12px 0 12px";
  const winMet = win ? p.kpis.filter((k) => k.raw.window?.metGoal).length : 0;
  const winD = win
    ? p.kpis.filter((k) => k.raw.window?.value != null).length
    : 0;
  const glOnly = only === "gleague" && p.gleague;
  const tallyBig = glOnly
    ? `${p.gleague!.pm} of ${p.gleague!.pd}`
    : win
      ? `${winMet} of ${winD}`
      : p.played
        ? p.tally || "—"
        : p.garbageOnly
          ? "—"
          : two && p.gleague
            ? `${p.gleague.pm} of ${p.gleague.pd}`
            : p.noGames
              ? "—"
              : "DNP";
  const tallyLabel = glOnly
    ? "goals met · G League game"
    : win
      ? `goals met · ${win.label.toLowerCase()}`
      : p.played
        ? "goals met tonight"
        : p.garbageOnly
          ? "garbage time only"
          : two
            ? "goals met · G League game"
            : p.noGames
              ? "no games this season"
              : "did not play";
  const recordTxt = (s: WindowHead["summary"]) =>
    s
      ? ` · ${s.wins}–${s.losses}, ${s.pointDiff > 0 ? "+" : ""}${s.pointDiff}`
      : "";
  const tonightLine = glOnly
    ? `G League · ${g.line} · ${p.gleague!.poss ?? "—"} poss`
    : winHead
      ? `${winHead.line}${recordTxt(winHead.summary)}`
      : win
        ? `${win.label} · through ${g.line}`
        : p.played
          ? `${g.line} · ${p.poss ?? "—"} poss`
          : p.garbageOnly
            ? `Garbage time only · ${p.garbagePoss ?? 0} poss · ${g.line}`
            : `Did not play · ${g.line}${p.twoWay ? " · two-way" : ""}`;

  const sheets = split ? [[blocks[0]], blocks.slice(1)] : [blocks];
  return (
    <>
      {sheets.map((sheet, si) => (
        <section className="page" data-screen-label="Player page" key={si}>
          <div className="ph">
            <div className="rpt">
              {winHead ? "KPI Dashboard" : "Postgame Report"}{" "}
              <span className="yr">· {winHead ? winHead.line : g.line}</span>
            </div>
            <div className="who">Player page · {p.name}</div>
          </div>
          {si === 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginBottom: 6,
              }}
            >
              <Headshot src={p.img} size="0.9in" />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    ...KICK,
                    letterSpacing: "0.14em",
                    whiteSpace: "nowrap",
                  }}
                >
                  Player development · {p.emphasis}
                </div>
                <div
                  style={{
                    fontFamily: DISP,
                    fontWeight: 800,
                    fontSize: 34,
                    lineHeight: 0.86,
                    textTransform: "uppercase",
                    marginTop: 8,
                  }}
                >
                  {p.name}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    lineHeight: 1,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#3a3a3a",
                    marginTop: 8,
                    whiteSpace: "nowrap",
                  }}
                >
                  {tonightLine}
                </div>
              </div>
              <div
                style={{
                  border: "2px solid #000",
                  padding: "6px 14px 7px",
                  background: "#fff",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontFamily: DISP,
                    fontWeight: 800,
                    fontSize: 34,
                    lineHeight: 0.9,
                  }}
                >
                  {tallyBig}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "#3a3a3a",
                    marginTop: 4,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  {tallyLabel}
                </div>
              </div>
            </div>
          )}

          {sheet.map((b, bi) => (
            <div key={b.title}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 10,
                  borderTop: "2px solid #000",
                  paddingTop: 4,
                  marginTop: bi ? b.mt : 0,
                }}
              >
                <span
                  style={{
                    fontFamily: DISP,
                    fontWeight: 800,
                    fontSize: 17,
                    textTransform: "uppercase",
                    lineHeight: 1,
                  }}
                >
                  {b.title}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: "#3a3a3a",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {b.sub}
                </span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    color: "#6b6864",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {b.note}
                </span>
              </div>
              {!b.full ? (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3,minmax(0,1fr))",
                      gap: 8,
                      marginTop: 6,
                    }}
                  >
                    {b.kpis.map((k) => (
                      <div
                        key={k.key}
                        style={{
                          border: "1px solid #cfccc8",
                          padding: "6px 9px 7px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontFamily: DISP,
                              fontWeight: 800,
                              fontSize: 14,
                              textTransform: "uppercase",
                              lineHeight: 1,
                            }}
                          >
                            {k.label}
                          </span>
                          <span style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                            Goal <b>{k.goal}</b>
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#6b6864",
                            marginTop: 3,
                          }}
                        >
                          {k.lastSeason && k.lastSeason.v !== "—"
                            ? `Last season ${k.lastSeason.v} · ${k.lastSeason.p} pct`
                            : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: "#6b6864", marginTop: 5 }}>
                    No NBA games this season.
                  </div>
                </>
              ) : (
                b.kpis.map((k) => (
                  <KpiRow
                    key={k.key}
                    k={k}
                    b={b}
                    p={p}
                    preseason={preseason && b.league === "NBA"}
                    CW={CW}
                    CH={CH}
                    kpad={kpad}
                    hideStd={
                      !!k.raw.window &&
                      data.meta.window?.selected?.kind === "season"
                    }
                  />
                ))
              )}
            </div>
          ))}
          <div className="fn" style={{ marginTop: 6 }} />
          <Footer
            left={
              winHead ? "Portland Trail Blazers · KPI Dashboard" : undefined
            }
            right={`${p.name} · ${winHead ? winHead.line : g.line}${sheets.length > 1 ? ` · ${si + 1} of ${sheets.length}` : ""}`}
          />
        </section>
      ))}
    </>
  );
}

function KpiRow({
  k,
  b,
  p,
  preseason,
  CW,
  CH,
  kpad,
  hideStd = false,
}: {
  k: KpiModel;
  b: Block;
  p: PlayerModel;
  preseason: boolean;
  /** The Season window: the box already is the season to date (Trevor 2026-10-02). */
  hideStd?: boolean;
  CW: number;
  CH: number;
  kpad: string;
}) {
  const w = k.raw.window;
  // Tonight (or the window, on a dashboard)
  let mk = k.game.mark;
  let v = k.game.noAtt || k.game.unavail ? "—" : k.game.v;
  let text = k.game.text;
  let kicker = b.tonightKicker;
  if (w) {
    kicker = w.label;
    v = fmt(w.value, k.decimals);
    text = w.text;
    mk =
      w.value == null || w.metGoal == null
        ? "none"
        : w.metGoal
          ? "met"
          : "miss";
  } else if (!k.hasGame && !k.game.text) {
    v = "DNP";
    text = "did not play tonight";
  }
  const goalSub = (k.goalP ? `${k.goalP} pct` : "") + (k.hold ? " · hold" : "");
  let stdV = k.std.v;
  let stdSub = k.std.small
    ? "small sample"
    : k.std.pOrd
      ? `${k.std.pOrd} pct`
      : "";
  let stdTitle = "Season to date";
  if (!k.std.has) {
    stdV = "—";
    stdSub = "";
  }
  if (preseason && k.lastSeason) {
    stdTitle = k.lastSeason.season;
    stdV = k.lastSeason.v;
    stdSub = k.lastSeason.p ? `${k.lastSeason.p} pct` : "";
  }
  const hitShort = preseason ? "—" : k.hit.short || "—";
  const hitSub = preseason
    ? "regular season"
    : k.hit.pct != null
      ? `${k.hit.pct}% of games`
      : "";
  // Season, game by game: the API's strip (every team game, DNP / garbage-only greyed)
  const stripCells = preseason ? [] : k.raw.gameStrip || [];
  const lastTxt = k.hasGame && b.league !== "G League" ? "Tonight" : "Latest";
  // Chart: last 20 games vs goal
  const c = chart(k.series, k.goalRaw ?? 0, k.hib, CW, CH, MET.bg, k.decimals);
  const chartTitle = `${b.league === "G League" ? "G League · " : ""}Last ${k.series.length || 20} games`;
  const chartKey =
    k.series.length && k.series.length < 20
      ? `${k.series.length} games this season`
      : "";
  const xrTxt = `${c.xr} · ${b.league === "G League" ? "latest" : k.hasGame ? "tonight" : "latest"}`;
  const emptyTxt = k.game.unavail
    ? "Not tracked in the G League yet"
    : preseason
      ? "Regular season starts Oct 20"
      : "No games this season";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,54fr) minmax(0,46fr)",
        gap: "0 18px",
        padding: kpad,
        borderBottom: "1px solid #cfccc8",
        alignItems: "stretch",
      }}
    >
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span
            style={{
              fontFamily: DISP,
              fontWeight: 800,
              fontSize: 22,
              textTransform: "uppercase",
              lineHeight: 1,
            }}
          >
            {k.label}
          </span>
          <span style={{ fontSize: 10, color: "#6b6864" }}>{k.unit}</span>
        </div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#3a3a3a",
            marginTop: 4,
          }}
        >
          {k.headline}
        </div>
        <div
          style={
            {
              fontSize: 10,
              lineHeight: 1.35,
              color: "#6b6864",
              marginTop: 1,
              textWrap: "pretty",
            } as CSSProperties
          }
        >
          {k.desc}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${hideStd ? 3 : 4},minmax(0,1fr))`,
            marginTop: 10,
            borderTop: "1px solid #e6e4e1",
            borderBottom: "1px solid #e6e4e1",
          }}
        >
          <div
            style={{
              padding: "7px 6px 8px",
              paddingLeft: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={KICK}>{kicker}</div>
            <div style={{ marginTop: 6 }}>
              <span style={blockStyle(mk, "3px 8px 3px 7px")}>
                <Glyph m={mk} size={10} />
                <b
                  style={{
                    fontFamily: DISP,
                    fontWeight: 800,
                    fontSize: 20,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {v}
                </b>
              </span>
            </div>
            {/* Wraps rather than overflowing into the Goal cell ("17 of 124 opp rim attempts" over a window). */}
            <div
              style={{
                ...SUB,
                marginTop: 5,
                whiteSpace: "normal",
                lineHeight: 1.15,
              }}
            >
              {text}
            </div>
          </div>
          <Cell title="Goal" big={k.goal} sub={goalSub} />
          {!hideStd && <Cell title={stdTitle} big={stdV} sub={stdSub} />}
          <Cell title="Hit rate" big={hitShort} sub={hitSub} last />
        </div>
        {stripCells.length > 0 && (
          <GameStrip
            cells={stripCells}
            title={`${b.league === "G League" ? "G League season" : "Season"}, game by game`}
            lastTxt={lastTxt}
          />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 10,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "#6b6864",
            fontWeight: 700,
          }}
        >
          <span>{chartTitle}</span>
          <span>{chartKey}</span>
        </div>
        {!c.empty && k.goalRaw != null ? (
          <div style={{ position: "relative", marginTop: 3 }}>
            <svg
              viewBox={`0 0 ${CW} ${CH}`}
              style={{ width: "100%", display: "block" }}
            >
              <rect
                x={c.good.x}
                y={c.good.y}
                width={c.good.w}
                height={c.good.h}
                fill="#f4f3f1"
              />
              {c.ticks.map((t, i) => (
                <line
                  key={i}
                  x1={c.x0}
                  x2={c.x1}
                  y1={t.y}
                  y2={t.y}
                  stroke="#dcd9d5"
                  strokeWidth={0.6}
                />
              ))}
              <line
                x1={c.x0}
                x2={c.x1}
                y1={c.goalY}
                y2={c.goalY}
                stroke="#1a1a1a"
                strokeWidth={1}
                strokeDasharray="3 2.5"
              />
              <path
                d={c.avg}
                fill="none"
                stroke="#8a8a8a"
                strokeWidth={1.6}
                strokeLinejoin="round"
              />
              {c.pts.map((d, i) => (
                <circle
                  key={i}
                  cx={d.cx}
                  cy={d.cy}
                  r={d.r}
                  fill={d.fill}
                  stroke="#1a1a1a"
                  strokeWidth={d.sw}
                />
              ))}
            </svg>
            {c.ticks.map((t, i) => (
              <span
                key={i}
                style={{
                  ...LBL,
                  right: pct(CW - c.x0 + 4, CW),
                  top: pct(t.y, CH),
                  transform: "translateY(-50%)",
                }}
              >
                {t.t}
              </span>
            ))}
            <span
              style={{
                ...LBL,
                left: pct(c.x1 + 10, CW),
                top: pct(c.goalY, CH),
                transform: "translateY(-50%)",
                fontWeight: 700,
                color: "#1a1a1a",
                lineHeight: 1.1,
              }}
            >
              Goal
              <br />
              {k.goalV}
            </span>
            <span
              style={{ ...LBL, left: pct(c.x0, CW), top: pct(c.yb + 3, CH) }}
            >
              {c.xl}
            </span>
            <span
              style={{
                ...LBL,
                right: pct(CW - c.x1, CW),
                top: pct(c.yb + 3, CH),
              }}
            >
              {xrTxt}
            </span>
          </div>
        ) : (
          <div
            style={{
              height: CH,
              marginTop: 3,
              border: "1px dashed #cfccc8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              color: "#6b6864",
            }}
          >
            {emptyTxt}
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({
  title,
  big,
  sub,
  last,
}: {
  title: string;
  big: string;
  sub: string;
  last?: boolean;
}) {
  return (
    <div
      style={{
        padding: "7px 6px 8px",
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid #e6e4e1",
        ...(last ? { paddingRight: 0 } : {}),
      }}
    >
      <div style={KICK}>{title}</div>
      <div style={BIG}>{big}</div>
      <div style={SUB}>{sub}</div>
    </div>
  );
}
